/**
 * FocusTrap — keeps keyboard focus inside a container while it's open.
 * Used by the Picker overlay. On unmount, focus returns to the previously
 * focused element (Animation Spec §8 — accessibility requirements).
 */

import { useEffect, useRef, type ReactNode } from 'react';

interface FocusTrapProps {
  active: boolean;
  children: ReactNode;
  /** Optional explicit element to focus on activation. */
  initialFocus?: () => HTMLElement | null;
  /** Optional element to return focus to on deactivation; defaults to whatever was focused before. */
  returnFocus?: () => HTMLElement | null;
}

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

export function FocusTrap({
  active,
  children,
  initialFocus,
  returnFocus,
}: FocusTrapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    if (!container) return;

    // Move focus inside the container.
    const target =
      initialFocus?.() ??
      (container.querySelector<HTMLElement>(FOCUSABLE) || container);
    target?.focus({ preventScroll: false });

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const nodes = container!.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes.length) {
        e.preventDefault();
        return;
      }
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !container!.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const back = returnFocus?.() ?? previouslyFocused.current;
      back?.focus?.();
    };
  }, [active, initialFocus, returnFocus]);

  return <div ref={containerRef}>{children}</div>;
}
